/**
 * DraftAssistPanel Component
 *
 * Story BL-045.4: Proposal Drafting Assistant
 *
 * Provides AI-powered suggestions for improving governance proposal drafts.
 * Uses Mistral 7B via oracle-bridge to analyze proposal content and suggest
 * improvements across motivation, budget, timeline, success criteria, and risks.
 *
 * Features:
 * - "Get AI Suggestions" button with minimum content threshold
 * - Loading state with cold-start warning
 * - Suggestion cards with severity badges (missing/weak/tip)
 * - Accept (copy to clipboard) and Dismiss per suggestion
 * - Graceful unavailable state
 * - All suggestions labeled "AI-generated" with Sparkles icon
 *
 * @module components/governance/DraftAssistPanel
 * @see AC6, AC7, AC8, AC9
 */

import { useState, useCallback } from 'react';
import { Sparkles, Loader2, Info, CheckCircle, X, Copy, Check } from 'lucide-react';
import { getDraftSuggestions, type DraftSuggestion } from '../../services/draftAssistService';

// =============================================================================
// Props
// =============================================================================

export interface DraftAssistPanelProps {
  /** Optional proposal title */
  title?: string;
  /** Current proposal body/content (required) */
  body: string;
  /** Minimum chars before showing button (default: 50) */
  minLength?: number;
  /** Optional class name */
  className?: string;
}

// =============================================================================
// Severity Badge Styles
// =============================================================================

const SEVERITY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  missing: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-300',
    label: 'Missing',
  },
  weak: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-300',
    label: 'Weak',
  },
  tip: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300',
    label: 'Tip',
  },
};

const SECTION_LABELS: Record<string, string> = {
  motivation: 'Motivation',
  budget: 'Budget',
  timeline: 'Timeline',
  success_criteria: 'Success Criteria',
  risks: 'Risks',
};

// =============================================================================
// Component
// =============================================================================

/**
 * DraftAssistPanel - AI-powered proposal draft improvement suggestions
 */
export function DraftAssistPanel({
  title,
  body,
  minLength = 50,
  className = '',
}: DraftAssistPanelProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<DraftSuggestion[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [accepted, setAccepted] = useState<Set<number>>(new Set());
  const [hasRequested, setHasRequested] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const meetsMinLength = body.length >= minLength;

  const handleFetchSuggestions = useCallback(async () => {
    setIsLoading(true);
    setDismissed(new Set());
    setAccepted(new Set());

    try {
      const result = await getDraftSuggestions(title ?? '', body);
      setSuggestions(result.suggestions);
      setUnavailable(result.model === 'unavailable');
      setHasRequested(true);
    } catch {
      setSuggestions([]);
      setUnavailable(true);
      setHasRequested(true);
    } finally {
      setIsLoading(false);
    }
  }, [title, body]);

  const handleAccept = useCallback(async (index: number, suggestion: string) => {
    try {
      await navigator.clipboard.writeText(suggestion);
    } catch {
      // Clipboard API may not be available in all contexts; still mark accepted
      // so the user sees the accepted state even if the silent copy failed
    }
    // Always mark as accepted regardless of clipboard availability —
    // the "Accept" action signifies the user's intent to use this suggestion
    setAccepted((prev) => new Set(prev).add(index));
  }, []);

  const handleDismiss = useCallback((index: number) => {
    setDismissed((prev) => new Set(prev).add(index));
  }, []);

  // Filter visible suggestions
  const visibleSuggestions = suggestions.filter(
    (_, i) => !dismissed.has(i),
  );

  return (
    <div className={`space-y-3 ${className}`} data-testid="draft-assist-panel">
      {/* Button */}
      <button
        onClick={handleFetchSuggestions}
        disabled={!meetsMinLength || isLoading}
        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
          meetsMinLength && !isLoading
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-muted-foreground cursor-not-allowed'
        }`}
        title={!meetsMinLength ? 'Add more content to enable AI suggestions' : undefined}
        data-testid="draft-assist-button"
      >
        <Sparkles className="h-4 w-4" />
        {isLoading ? 'Analyzing...' : 'Get AI Suggestions'}
      </button>

      {/* Loading state */}
      {isLoading && (
        <div
          className="flex items-center gap-2 text-sm text-muted-foreground py-2"
          data-testid="draft-assist-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Asking Mistral 7B... (may take 10-15s on first use)</span>
        </div>
      )}

      {/* Unavailable state */}
      {unavailable && hasRequested && !isLoading && (
        <div
          className="flex items-center gap-2 text-sm text-muted-foreground py-2"
          data-testid="draft-assist-unavailable"
        >
          <Info className="h-4 w-4" />
          <span>AI suggestions unavailable — Mistral 7B is offline. Try again later.</span>
        </div>
      )}

      {/* Complete state — no suggestions needed */}
      {suggestions.length === 0 && !unavailable && hasRequested && !isLoading && (
        <div
          className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 py-2"
          data-testid="draft-assist-complete"
        >
          <CheckCircle className="h-4 w-4" />
          <span>Your proposal looks complete! No suggestions at this time.</span>
        </div>
      )}

      {/* Suggestion cards */}
      {visibleSuggestions.length > 0 && !isLoading && (
        <div className="space-y-3">
          {suggestions.map((suggestion, index) => {
            if (dismissed.has(index)) return null;

            const severity = SEVERITY_STYLES[suggestion.severity] ?? SEVERITY_STYLES.tip;
            const sectionLabel = SECTION_LABELS[suggestion.section] ?? suggestion.section;
            const isAccepted = accepted.has(index);

            return (
              <div
                key={index}
                className={`rounded-lg border p-4 space-y-2 ${
                  isAccepted
                    ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20'
                    : 'border-border bg-card'
                }`}
                data-testid={`draft-assist-suggestion-${index}`}
              >
                {/* Header: section + severity badge + AI label */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${severity.bg} ${severity.text}`}>
                      {severity.label}
                    </span>
                    <span className="text-sm font-medium text-foreground">{sectionLabel}</span>
                  </div>
                  <div
                    className="flex items-center gap-1 text-xs text-muted-foreground"
                    data-testid="draft-assist-ai-label"
                  >
                    <Sparkles className="h-3 w-3" />
                    <span>AI-generated</span>
                  </div>
                </div>

                {/* Suggestion text */}
                <p className="text-sm text-foreground/80">{suggestion.suggestion}</p>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-1">
                  {isAccepted ? (
                    <div className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
                      <Check className="h-4 w-4" />
                      <span>Copied to clipboard</span>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleAccept(index, suggestion.suggestion)}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        data-testid={`draft-assist-accept-${index}`}
                      >
                        <Copy className="h-3 w-3" />
                        Accept
                      </button>
                      <button
                        onClick={() => handleDismiss(index)}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-muted transition-colors"
                        data-testid={`draft-assist-dismiss-${index}`}
                      >
                        <X className="h-3 w-3" />
                        Dismiss
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DraftAssistPanel;
